
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, History, Pencil, Send } from "lucide-react";
import EmailComposer from '@/components/email-center/EmailComposer';
import EmailTemplates from '@/components/email-center/EmailTemplates';
import EmailHistory from '@/components/email-center/EmailHistory';

type EmailCenterTab = 'compose' | 'templates' | 'history';

interface EmailCenterHeroProps {
  activeTab: EmailCenterTab;
}

const EmailCenterHero: React.FC<EmailCenterHeroProps> = ({ activeTab }) => {
  return (
    <header className="sticky top-0 z-20 bg-gray-50 pb-4 pt-4">
      <div className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_18px_48px_rgba(15,23,42,0.06)]">
        <div className="relative min-h-[200px] overflow-hidden border-b border-slate-200 bg-[radial-gradient(circle_at_78%_35%,rgba(168,85,247,0.12),transparent_34%),linear-gradient(100deg,#ffffff_0%,#ffffff_46%,#fdf9ff_100%)] px-8 py-8 md:px-12 lg:px-16">
          <div className="relative z-10 flex max-w-2xl items-start gap-7">
            <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[18px] bg-[rgb(236,230,250)] shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]">
              <Send className="h-7 w-7 -rotate-10 text-[rgb(91,45,240)]" strokeWidth={2.4} />
            </div>
            <div>
              <p className="mb-3 text-sm font-bold uppercase tracking-[0.38em] text-[rgb(108,70,243)]">
                Email Center
              </p>
              <h1
                style={{ lineHeight: '2rem', fontWeight: 300 }}
                className="max-w-xl text-3xl sm:text-4xl lg:text-[42px] tracking-normal text-slate-900 font-['Canela',_serif] flex flex-col gap-y-[0.1em]"
              >
                <span>Smart email outreach,</span>
                <span>made simple</span>
              </h1>
              <p className="mt-4 max-w-lg text-xl font-medium leading-relaxed text-slate-500">
                Send personalized bulk emails to your contacts,
                <br className="hidden sm:block" />
                influencers, and brands.
              </p>
            </div>
          </div>

          <div className="pointer-events-none absolute right-[2%] top-12 hidden h-48 w-[400px] origin-right scale-[0.65] md:block sm:scale-75 lg:right-[6%] lg:scale-[0.85] xl:right-[10%] xl:scale-100 transition-all duration-300">

            {/* Purple Envelope */}
            <div className="absolute right-20 top-6 h-[118px] w-[168px] rotate-[-7deg] rounded-[18px] bg-gradient-to-br from-violet-300 via-purple-500 to-violet-700 shadow-[0_18px_24px_rgba(109,40,217,0.22)]">
              <div className="absolute inset-x-0 top-0 h-[78px] rounded-t-[18px] bg-gradient-to-br from-violet-300 via-purple-400 to-violet-700 [clip-path:polygon(0_0,100%_0,50%_82%)]" />
              <div className="absolute inset-x-0 bottom-0 h-[76px] rounded-b-[18px] bg-gradient-to-tr from-violet-600 via-purple-500 to-violet-300 [clip-path:polygon(0_100%,50%_24%,100%_100%)]" />
            </div>

            {/* Checkmark Badge */}
            <div className="absolute right-10 top-[92px] flex h-[52px] w-[52px] items-center justify-center rounded-full bg-white shadow-[0_14px_24px_rgba(88,28,135,0.18)]">
              <svg className="h-7 w-7 text-purple-600" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12.5l4.4 4.4L19 7.3" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>

            {/* Green Envelope */}
            <div className="absolute right-[-52px] top-[86px] h-[44px] w-[58px] rotate-[12deg] drop-shadow-[0_8px_16px_rgba(16,185,129,0.25)]">
              <svg width="100%" height="100%" viewBox="0 0 120 85" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="bgMint" x1="0" y1="0" x2="0" y2="85" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#6EE7B7" />
                    <stop offset="100%" stop-color="#34D399" />
                  </linearGradient>

                  <linearGradient id="sideMint" x1="0" y1="0" x2="60" y2="42" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#A7F3D0" />
                    <stop offset="100%" stop-color="#6EE7B7" />
                  </linearGradient>

                  <linearGradient id="bottomMint" x1="60" y1="85" x2="60" y2="40" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#D1FAE5" />
                    <stop offset="100%" stop-color="#6EE7B7" />
                  </linearGradient>

                  <linearGradient id="topMint" x1="60" y1="0" x2="60" y2="50" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stop-color="#FFFFFF" />
                    <stop offset="100%" stop-color="#A7F3D0" />
                  </linearGradient>

                  <filter id="topShadow" x="-10%" y="-10%" width="120%" height="120%">
                    <feDropShadow dx="0" dy="2.5" stdDeviation="2" flood-color="#064E3B" flood-opacity="0.12" />
                  </filter>

                  <filter id="bottomShadow" x="-10%" y="-10%" width="120%" height="120%">
                    <feDropShadow dx="0" dy="-1.5" stdDeviation="1.5" flood-color="#064E3B" flood-opacity="0.08" />
                  </filter>
                </defs>

                <rect x="0" y="0" width="120" height="85" rx="8" fill="url(#bgMint)" />

                <path d="M0,8 L56,44 L0,77 Z" fill="url(#sideMint)" />

                <path d="M120,8 L64,44 L120,77 Z" fill="url(#sideMint)" />

                <path d="M0,77 C0,81.5 3.5,85 8,85 L112,85 C116.5,85 120,81.5 120,77 L64,41 C61.5,39.5 58.5,39.5 56,41 Z" fill="url(#bottomMint)" filter="url(#bottomShadow)" />

                <path d="M0.5,77 L56,41.5" stroke="#FFFFFF" stroke-width="0.8" opacity="0.6" />
                <path d="M119.5,77 L64,41.5" stroke="#FFFFFF" stroke-width="0.8" opacity="0.6" />

                <path d="M0,8 C0,3.5 3.5,0 8,0 L112,0 C116.5,0 120,3.5 120,8 L64,48 C61.5,49.5 58.5,49.5 56,48 Z" fill="url(#topMint)" filter="url(#topShadow)" />

                <rect x="0.5" y="0.5" width="119" height="84" rx="7.5" stroke="#FFFFFF" stroke-width="1" opacity="0.4" fill="none" />
              </svg>
            </div>

            {/* Sparkles */}
            <div className="pointer-events-none absolute left-[80px] top-[10px] text-purple-400">
              <Sparkle className="h-9 w-9" />
            </div>
            <div className="pointer-events-none absolute right-[10px] top-[10px] text-purple-400">
              <Sparkle className="h-8 w-8" />
            </div>
            <div className="pointer-events-none absolute bottom-[10px] left-[15px] text-purple-300">
              <Sparkle className="h-7 w-7" />
            </div>
          </div>
        </div>

        <div className="bg-white px-4 py-6 md:px-6 md:py-8">
          <TabsList className="inline-flex h-14 w-auto items-stretch rounded-xl border border-slate-200 bg-white p-0 shadow-sm overflow-hidden">
            <TabsTrigger
              value="compose"
              className="relative flex items-center justify-center bg-transparent transition-all duration-200 text-slate-800 data-[state=active]:!text-[#A655F8] data-[state=active]:!shadow-none focus-visible:outline-none py-0 px-4"
              aria-current={activeTab === 'compose' ? 'page' : undefined}
            >
              <div className="relative flex items-center gap-1.5 md:gap-2 h-full">
                <Pencil className="h-4 w-4 md:h-4.5 md:w-4.5" />
                <span className="text-xs sm:text-sm md:text-[15px] font-semibold whitespace-nowrap">Compose Email</span>
                {activeTab === 'compose' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full bg-[#A655F8]" />
                )}
              </div>
            </TabsTrigger>

            <div className="h-6 w-[1px] bg-slate-200 self-center shrink-0" />

            <TabsTrigger
              value="templates"
              className="relative flex items-center justify-center bg-transparent transition-all duration-200 text-slate-800 data-[state=active]:!text-[#A655F8] data-[state=active]:!shadow-none focus-visible:outline-none py-0 px-4"
              aria-current={activeTab === 'templates' ? 'page' : undefined}
            >
              <div className="relative flex items-center gap-1.5 md:gap-2 h-full">
                <FileText className="h-4 w-4 md:h-4.5 md:w-4.5" />
                <span className="text-xs sm:text-sm md:text-[15px] font-semibold whitespace-nowrap">Templates</span>
                {activeTab === 'templates' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full bg-[#A655F8]" />
                )}
              </div>
            </TabsTrigger>

            <div className="h-6 w-[1px] bg-slate-200 self-center shrink-0" />

            <TabsTrigger
              value="history"
              className="relative flex items-center justify-center bg-transparent transition-all duration-200 text-slate-800 data-[state=active]:!text-[#A655F8] data-[state=active]:!shadow-none focus-visible:outline-none py-0 px-4"
              aria-current={activeTab === 'history' ? 'page' : undefined}
            >
              <div className="relative flex items-center gap-1.5 md:gap-2 h-full">
                <History className="h-4 w-4 md:h-4.5 md:w-4.5" />
                <span className="text-xs sm:text-sm md:text-[15px] font-semibold whitespace-nowrap">Email History</span>
                {activeTab === 'history' && (
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-t-full bg-[#A655F8]" />
                )}
              </div>
            </TabsTrigger>
          </TabsList>
        </div>
      </div>
    </header>
  );
};

interface SparkleProps {
  className?: string;
}

const Sparkle: React.FC<SparkleProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" fill="none" aria-hidden="true">
    <path
      d="M24 3c2.7 12 4.7 14 21 21-16.3 7-18.3 9-21 21C21.3 33 19.3 31 3 24 19.3 17 21.3 15 24 3Z"
      fill="currentColor"
    />
  </svg>
);

const EmailCenter: React.FC = () => {
  const [activeTab, setActiveTab] = useState<EmailCenterTab>('compose');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 py-6 sm:px-6 lg:mx-auto lg:w-[100%]">
        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as EmailCenterTab)} className="space-y-6">
          <EmailCenterHero activeTab={activeTab} />

          <TabsContent value="compose">
            <EmailComposer />
          </TabsContent>

          <TabsContent value="templates">
            <EmailTemplates />
          </TabsContent>

          <TabsContent value="history">
            <EmailHistory />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EmailCenter;
